import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import {
  Icon,
  InlineMessage,
  InputField,
  Typography,
} from '@saviynt/design-system';

import testIds from '../../../../constants/testIds';
import { type RootState as HostRoot } from '../../../../services/serviceTypes';
import extractValidationPattern, {
  type ParsedValidationResult,
} from '../../../../utils/extractValidationPattern';

interface FormInputTextProps {
  label: string | null;
  defaultValue: string | undefined;
  password: boolean;
  required: boolean;
  pattern: string | null;
  guideMe: boolean;
  fieldDescription: string | null;
  payloadAtrributeName: string;
  attributeName: string;
  fieldDisabled: boolean;
  inputValueStatus: (status: {
    attributeName: string;
    value: string;
    hasError: boolean;
    payloadAtrributeName: string;
  }) => void;
}

/**
 * Form Input Text Field
 */
function FormInputText({
  label,
  defaultValue,
  password,
  required,
  pattern,
  guideMe,
  fieldDescription,
  payloadAtrributeName,
  attributeName,
  fieldDisabled,
  inputValueStatus,
}: FormInputTextProps) {
  const intl = useIntl();
  const [inputValue, setInputValue] = useState(defaultValue || '');
  const [critical, setCritical] = useState(false);
  const [visible, setVisible] = useState(!password);
  const [validInput, setValidInput] = useState(true);
  const [hasError, setHasError] = useState(false);
  const { messages } = useSelector((state: HostRoot) => state.user);

  const patternObject: ParsedValidationResult = pattern
    ? extractValidationPattern(pattern)
    : null;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (patternObject?.type === 'patterns') {
      const regex = new RegExp(patternObject.patterns[0].pattern);

      setValidInput(regex.test(value));
      setHasError(!regex.test(value));
    } else {
      setValidInput(true); // Reset to true for other cases
    }

    setInputValue(value);
  };

  const handleMaxLength = (value: ParsedValidationResult): number => {
    if (value?.type === 'maxLength') {
      return Number(value.maxLength);
    }

    return 999;
  };

  const handleValidationMessageString = (): string => {
    let validationString = '';

    if (patternObject?.type === 'maxLength') {
      if (inputValue.length === Number(patternObject.maxLength)) {
        validationString = intl.formatMessage({
          id: 'INTEGRATIONS.SERVICENOW.INPUT.MAXLENGTH',
          defaultMessage: `Maximum ${patternObject.maxLength} is Allowed`,
        });
      }
    }

    if (patternObject?.type === 'patterns') {
      const regexExp = patternObject.patterns[0].pattern;
      const errorMessage = patternObject.patterns[0].error
        ? messages[patternObject.patterns[0].error]
        : patternObject.patterns[0].error;

      if (regexExp) {
        const regex = new RegExp(regexExp);

        if (!regex.test(inputValue)) {
          validationString = intl.formatMessage({
            id: errorMessage,
          });
        }
      }
    }

    return validationString;
  };

  const handleValidationMessage = (
    patternValue: ParsedValidationResult
  ): React.ReactNode => {
    if (patternValue === null) {
      return null;
    }

    if (
      patternValue.type === 'maxLength' &&
      inputValue.length === Number(patternValue.maxLength)
    ) {
      return (
        <InlineMessage
          text={handleValidationMessageString()}
          colorTheme='critical'
          size='small'
          leftIcon={<Icon kind='AlertCriticalFilled' />}
        />
      );
    }

    if (patternValue.type === 'patterns') {
      return (
        <InlineMessage
          text={handleValidationMessageString()}
          colorTheme='critical'
          size='small'
          leftIcon={<Icon kind='AlertCriticalFilled' />}
        />
      );
    }

    return null;
  };

  useEffect(() => {
    if (
      patternObject?.type === 'maxLength' &&
      inputValue.length === Number(patternObject.maxLength)
    ) {
      setCritical(true);
    } else if (!validInput && patternObject?.type === 'patterns') {
      setCritical(true);
    } else {
      setCritical(false);
    }
  }, [inputValue, patternObject, validInput]);

  useEffect(() => {
    inputValueStatus({
      attributeName,
      value: inputValue,
      hasError,
      payloadAtrributeName,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasError, inputValue]);

  const handleClear = (value: string) => {
    if (value === '') {
      setInputValue('');
      setValidInput(true);
      setHasError(false);
    }
  };

  return (
    <InputField
      dataTestId={`${testIds.DISCOVERY_FORM_BUILDER_INPUT_FIELD}--${attributeName}`}
      name='basic'
      placeholder=''
      label={
        <Typography
          color={
            fieldDisabled
              ? '--color-foreground-neutral-subtle'
              : '--color-foreground-neutral-bold'
          }
          kind='label'
          htmlFor='Critical'>
          {label}
        </Typography>
      }
      shouldDisallowBrackets={false}
      value={inputValue}
      setValue={handleClear}
      onChange={handleInputChange}
      maxLength={handleMaxLength(patternObject)}
      isRequired={required}
      isCritical={critical}
      isDisabled={fieldDisabled}
      setIsCritical={setCritical}
      suffixButtonType={password ? 'password' : undefined}
      suffixIsToggle={!visible}
      type={visible ? 'text' : 'password'}
      suffixOnClick={() => setVisible(!visible)}
      CriticalHelperText={handleValidationMessage(patternObject)}
      HelperText={
        guideMe && (
          <div data-testid='helper-text'>
            {fieldDescription?.split('\n').map((line) => (
              <Typography
                kind='body3'
                color='--color-foreground-neutral-medium'>
                {line}
              </Typography>
            ))}
          </div>
        )
      }
    />
  );
}

export default FormInputText;
